import { ContactMessageEntry } from '../../../../../../services/marketplace/generated/kubb/models/ContactMessageEntry';
import { toSingleLine } from '../../../../../../utils/strings';
import { LeadStatusSource, deriveListingStatus } from '../../listingStatus';
import { InactiveCardView, MarketplaceOffer, OfferCardView } from '../offers/utils';

export type WonConversation = {
  kind: 'won';
  dealershipName: string;
  repName?: string | null;
  amount?: number | null;
  acceptedAt?: string | null;
  history?: MarketplaceOffer[];
};

export type PlatformConversation = { kind: 'platform'; contextNote?: string };

// The dealer whose conversation is open, re-derived live each poll (see getLiveTarget). The
// discriminant says whether it's a live offer card or a dead/inactive one.
export type ConversationTarget =
  | { kind: 'active'; card: OfferCardView }
  | { kind: 'inactive'; card: InactiveCardView }
  | WonConversation
  | PlatformConversation;

export type OfferConversationTarget = Extract<ConversationTarget, { kind: 'active' | 'inactive' }>;

// Re-derives the open conversation's card from the freshest poll, falling back to the open-time
// snapshot so the drawer never force-closes when a poll drops the dealer's card.
export const getLiveTarget = (
  conversation: OfferConversationTarget | null,
  activeCards: OfferCardView[],
  inactiveCards: InactiveCardView[],
): ConversationTarget | null => {
  if (conversation == null) return null;

  const { dealerLabel } = conversation.card;
  const activeCard = activeCards.find((card) => card.dealerLabel === dealerLabel);
  if (activeCard != null) return { kind: 'active', card: activeCard };

  const inactiveCard = inactiveCards.find((card) => card.dealerLabel === dealerLabel);
  if (inactiveCard != null) return { kind: 'inactive', card: inactiveCard };

  return conversation;
};

// The latest message shown on a dealer's offer-card entry point. Optimistic and failed-send
// handling arrives with the composer (LE-18029) — there is no message status field yet.
export type MessagePreview = {
  senderRole: ContactMessageEntry['senderRole'];
  text: string;
};

// The driver never sees the rep's real name or orgName for platform messages, so we pin a brand
// label. Single source so the bubble label and preview prefix can't drift on a rename.
export const PLATFORM_LABEL = 'Lease End';

export const isPlatformChatAvailable = (
  lead: LeadStatusSource,
): lead is NonNullable<LeadStatusSource> =>
  lead != null && deriveListingStatus(lead) !== 'building';

export const targetLabel = (target: ConversationTarget): string => {
  if (target.kind === 'won') return target.dealershipName;
  if (target.kind === 'platform') return PLATFORM_LABEL;
  return target.card.dealerLabel;
};

export const targetHistory = (
  target: ConversationTarget | null,
): MarketplaceOffer[] | undefined => {
  if (target == null || target.kind === 'platform') return undefined;
  if (target.kind === 'won') return target.history;
  return target.card.history;
};

// Groups a lead's messages by orgName (the thread's counterparty, which matches the offer card's
// dealerLabel). orgName is null only on the create endpoint's just-sent echo, which we don't read
// here, so those are skipped. The platform house thread buckets under "Lease End", which no card's
// dealerLabel ever matches — it surfaces through the header's PlatformChat instead.
export const getMessagesByOrg = (
  messages?: ContactMessageEntry[],
): Map<string, ContactMessageEntry[]> => {
  const byOrg = new Map<string, ContactMessageEntry[]>();

  (messages ?? []).forEach((message) => {
    if (message.orgName == null) return;
    const thread = byOrg.get(message.orgName);
    if (thread) thread.push(message);
    else byOrg.set(message.orgName, [message]);
  });

  return byOrg;
};

// One entry in the converged timeline: a chat message or a money event (offer/counter row).
export type TimelineItem =
  | { kind: 'message'; message: ContactMessageEntry }
  | { kind: 'money'; offer: MarketplaceOffer };

// Positions by createdAt (sort key, day-divider key, and each card's timestamp) so the thread reads
// in event order and a reinstated offer keeps its spot instead of resurfacing as if it were new.
export const timelineTimestamp = (item: TimelineItem) =>
  item.kind === 'message' ? item.message.createdAt : item.offer.createdAt;

const timelineId = (item: TimelineItem) =>
  item.kind === 'message' ? item.message.id : item.offer.id;

export const getTimelineItems = (
  thread: ContactMessageEntry[] | undefined,
  history: MarketplaceOffer[] | undefined,
): TimelineItem[] => {
  const messageItems = (thread ?? []).map((message) => ({ kind: 'message' as const, message }));
  const moneyItems = (history ?? []).map((offer) => ({ kind: 'money' as const, offer }));

  return [...messageItems, ...moneyItems].sort((first, second) => {
    const byTime = timelineTimestamp(first).localeCompare(timelineTimestamp(second));
    if (byTime !== 0) return byTime;
    if (first.kind !== second.kind) return first.kind === 'money' ? -1 : 1;
    return timelineId(first).localeCompare(timelineId(second));
  });
};

const isUnreadMessage = (message: ContactMessageEntry): boolean =>
  message.seenAt == null && message.senderRole !== 'driver';

const isDriverSideOffer = (offer: MarketplaceOffer): boolean =>
  offer.type === 'driver_ask' || offer.type === 'driver_counter';

const hasNote = (offer: MarketplaceOffer): boolean => (offer.notes ?? '').trim() !== '';

export const isNotedDealerOffer = (offer: MarketplaceOffer): boolean =>
  hasNote(offer) && !isDriverSideOffer(offer);

const isUnreadNotedOffer = (offer: MarketplaceOffer): boolean =>
  offer.seenAt == null && isNotedDealerOffer(offer);

const isUnseenNotelessOffer = (offer: MarketplaceOffer): boolean =>
  offer.seenAt == null && !hasNote(offer) && !isDriverSideOffer(offer);

export const getMessagePreview = (
  thread?: ContactMessageEntry[],
  history?: MarketplaceOffer[],
): MessagePreview | undefined => {
  const previewable = getTimelineItems(thread, history).filter((item) =>
    item.kind === 'message' ? item.message.body.trim() !== '' : hasNote(item.offer),
  );

  const latest = previewable[previewable.length - 1];
  if (latest == null) return undefined;

  if (latest.kind === 'message') {
    return {
      senderRole: latest.message.senderRole,
      text: toSingleLine(latest.message.body),
    };
  }
  return {
    senderRole: latest.offer.type === 'driver_counter' ? 'driver' : 'dealer',
    text: toSingleLine(latest.offer.notes ?? ''),
  };
};

export const getUnreadCount = (
  thread?: ContactMessageEntry[],
  history?: MarketplaceOffer[],
): number =>
  (thread ?? []).filter(isUnreadMessage).length + (history ?? []).filter(isUnreadNotedOffer).length;

export const getUnseenNotelessOfferIds = (history?: MarketplaceOffer[]): string[] =>
  (history ?? []).filter(isUnseenNotelessOffer).map((offer) => offer.id);

export type UnreadThreadIds = { messages: string[]; offers: string[] };

export const getUnreadThreadIds = (
  thread?: ContactMessageEntry[],
  history?: MarketplaceOffer[],
): UnreadThreadIds => ({
  messages: (thread ?? []).filter(isUnreadMessage).map((message) => message.id),
  offers: (history ?? []).filter(isUnreadNotedOffer).map((offer) => offer.id),
});

// Where the dealer's negotiation stood when a messaging event fired, for analytics.
export const negotiationState = (target: ConversationTarget): string => {
  if (target.kind === 'platform') return 'platform';
  if (target.kind === 'won') return 'won_accepted';
  if (target.kind === 'inactive') return `inactive_${target.card.inactiveState}`;
  const { driverCounterAmount, respondedToCounterAmount, revivedRejectedCounterAmount } =
    target.card;
  if (driverCounterAmount != null) return 'driver_counter_pending';
  if (respondedToCounterAmount != null) return 'dealer_countered';
  if (revivedRejectedCounterAmount != null) return 'dealer_revived';
  return 'dealer_offer';
};

// Whether the driver started the conversation or is replying — a prior dealer message means reply.
export const initiatedOrReplied = (thread?: ContactMessageEntry[]): 'initiated' | 'replied' =>
  thread?.some((message) => message.senderRole === 'dealer') ? 'replied' : 'initiated';
